"use client";

import { useState } from "react";
import { countryName } from "@/lib/conditions/country-names";
import type { WeekPressure } from "@/lib/conditions/busyness-data";
import { pressureColour, pressureLabel, PRESSURE_LEGEND } from "@/lib/conditions/busyness-pressure";

type Props = {
  weeks: WeekPressure[];
};

function formatDate(iso: string): string {
  return new Date(iso + "T00:00:00Z").toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

function formatDateRange(start: string, end: string): string {
  return `${formatDate(start)} – ${formatDate(end)}`;
}

type MonthSpan = { label: string; span: number };

function deriveMonthLabels(weeks: WeekPressure[]): MonthSpan[] {
  const labels: MonthSpan[] = [];
  let currentMonth = "";

  for (const week of weeks) {
    const d = new Date(week.weekStart + "T00:00:00Z");
    const month = d.toLocaleDateString("en-GB", {
      month: "short",
      timeZone: "UTC",
    });

    if (month === currentMonth) {
      labels[labels.length - 1].span += 1;
    } else {
      currentMonth = month;
      labels.push({ label: month, span: 1 });
    }
  }

  return labels;
}

type ConsolidatedHoliday = {
  countryCode: string;
  holidayName: string;
  regions: string[];
  startDate: string;
  endDate: string;
};

function consolidateHolidays(holidays: WeekPressure["activeHolidays"]): ConsolidatedHoliday[] {
  const map = new Map<string, ConsolidatedHoliday>();

  for (const h of holidays) {
    const key = `${h.countryCode}|${h.holidayName}|${h.startDate}|${h.endDate}`;
    const existing = map.get(key);
    if (existing) {
      if (h.region && !existing.regions.includes(h.region)) {
        existing.regions.push(h.region);
      }
    } else {
      map.set(key, {
        countryCode: h.countryCode,
        holidayName: h.holidayName,
        regions: h.region ? [h.region] : [],
        startDate: h.startDate,
        endDate: h.endDate,
      });
    }
  }

  return Array.from(map.values());
}

function weekAriaLabel(week: WeekPressure, pct: number): string {
  const date = formatDate(week.weekStart);
  const level = pressureLabel(week.pressure);
  const hols = consolidateHolidays(week.activeHolidays)
    .map((h) => `${countryName(h.countryCode)} ${h.holidayName}`)
    .join(", ");
  return hols
    ? `Week of ${date}, ${pct}% crowd pressure (${level}). On holiday: ${hols}.`
    : `Week of ${date}, ${pct}% crowd pressure (${level}). No school holidays.`;
}

export default function AggregateHeatbar({ weeks }: Props) {
  const [activeWeek, setActiveWeek] = useState<WeekPressure | null>(null);
  const [pinned, setPinned] = useState(false);

  if (weeks.length === 0) return null;

  const monthLabels = deriveMonthLabels(weeks);

  return (
    <div>
      {/* Scrollable strip (prevents cramping on mobile) */}
      <div className="overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div style={{ minWidth: `${weeks.length * 20}px` }}>
          {/* Heat bar */}
          <div className="flex gap-1">
            {weeks.map((week) => {
              const pct = Math.round(week.pressure * 100);
              const isActive = activeWeek?.weekStart === week.weekStart;

              return (
                <button
                  key={week.weekStart}
                  type="button"
                  aria-pressed={pinned && isActive}
                  aria-label={weekAriaLabel(week, pct)}
                  className={`flex-1 flex flex-col items-center cursor-pointer transition-all rounded-sm p-0 bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/50 ${
                    isActive ? "scale-y-125" : "hover:scale-y-110"
                  }`}
                  onMouseEnter={() => {
                    if (!pinned) setActiveWeek(week);
                  }}
                  onMouseLeave={() => {
                    if (!pinned) setActiveWeek(null);
                  }}
                  onFocus={() => {
                    if (!pinned) setActiveWeek(week);
                  }}
                  onClick={() => {
                    if (pinned && isActive) {
                      setPinned(false);
                      setActiveWeek(null);
                    } else {
                      setPinned(true);
                      setActiveWeek(week);
                    }
                  }}
                >
                  <span className={`w-full h-7 rounded-sm ${pressureColour(week.pressure)}`} />
                  <span className="text-[9px] text-text-muted mt-1 leading-none tabular-nums">
                    {pct}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Month labels */}
          <div className="flex mt-2 border-t border-border-light pt-1.5">
            {monthLabels.map((m) => (
              <div
                key={m.label}
                className="text-[10px] font-medium text-text-muted text-center"
                style={{ flex: m.span }}
              >
                {m.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-[11px] text-text-muted">
        {PRESSURE_LEGEND.map((b) => (
          <div key={b.label} className="flex items-center gap-1">
            <span className={`w-3 h-3 rounded-sm ${b.colour}`} />
            {b.label}
          </div>
        ))}
        <span className="text-[10px] text-text-muted/60 ml-auto md:hidden">
          Tap a bar for details
        </span>
      </div>

      {/* Detail card */}
      {activeWeek && (
        <div role="status" aria-live="polite" className="mt-4 rounded-[12px] border border-border-light p-4 shadow-[0_4px_16px_rgba(0,0,0,0.06)] animate-fade-in bg-warm-bg/30">
          <div className="flex flex-col gap-1.5 mb-3 sm:flex-row sm:items-center sm:justify-between">
            <span className="font-serif text-[15px] font-semibold text-navy">
              Week of {formatDate(activeWeek.weekStart)}
            </span>
            <span className={`self-start px-2.5 py-0.5 rounded text-[11px] font-semibold text-white ${pressureColour(activeWeek.pressure)}`}>
              {pressureLabel(activeWeek.pressure)} &middot; {Math.round(activeWeek.pressure * 100)}% crowd pressure
            </span>
          </div>

          {activeWeek.activeHolidays.length > 0 ? (
            <div className="space-y-1.5">
              {consolidateHolidays(activeWeek.activeHolidays).map((h) => (
                <div key={`${h.countryCode}-${h.holidayName}-${h.startDate}`} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[12px]">
                  <span className="font-semibold text-navy shrink-0">
                    {countryName(h.countryCode)}
                    {h.regions.length > 0 && (
                      <span className="font-normal text-text-muted ml-0.5">
                        ({h.regions.join(", ")})
                      </span>
                    )}
                  </span>
                  <span className="text-text-muted">
                    {h.holidayName}
                  </span>
                  <span className="text-text-muted text-[11px] sm:ml-auto shrink-0">
                    {formatDateRange(h.startDate, h.endDate)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[12px] text-text-muted">No school holidays this week.</p>
          )}
        </div>
      )}
    </div>
  );
}
